import { S } from '../../domain/state.js';
import { explain, wTx } from '../../domain/explain.js';
import { fMoney, signed, wD } from '../../lib/format.js';
import { recMatchAll, recSrcOf, recStatusTxt, recCandidates, recFreqLabel, recEst, recOpen } from '../../domain/recurring.js';
import { revCandidates } from '../../domain/merchants.js';
import { Sheet, openWhy } from '../../components/ui.jsx';
import { openModal, closeModal, navigate } from '../../app/nav.js';
import { UI, useApp } from '../../store/ui.js';
import { recLink, recSkip, recUnskip, recUnlink, fixReversedNames, keepName } from '../../store/actions.js';
import { rangeDays } from '../../domain/forecast.js';
import { openRecModal } from '../settings/RecModal.jsx';

export function whyCtx() {
  return { viewKey: UI.viewKey, per: UI.flowPer, days: rangeDays(UI.flowPer), stress: UI.flowStress, sim: UI.flowSim };
}

export function openWhyModal(key, arg) {
  openModal(() => <WhySheet k={key} arg={arg} />);
}

function Row({ r }) {
  const val = r.txt != null ? r.txt : r.op ? fMoney(Math.abs(r.v)) : fMoney(r.v);
  return (
    <div className={'why-row' + (r.tot ? ' tot' : '')}>
      <span>{r.op && <i className="why-op">{r.op}</i>}{r.k}</span>
      <b dir="ltr">{val}</b>
    </div>
  );
}
export function WhyList({ list, title }) {
  if (!list || !list.length) return null;
  return (
    <>
      <div className="why-lt">{title} ({list.length})</div>
      <div>
        {list.map((x, i) => (
          <div className="why-li" key={i}>
            <div style={{ minWidth: 0 }}>
              <div className="why-ln">{x.n}</div>
              <div className="why-ls">{x.d}{x.s ? ' · ' + x.s : ''}</div>
            </div>
            <b dir="ltr" style={{ color: x.v > 0 ? 'var(--green)' : 'var(--ink)' }}>{signed(x.v)}</b>
          </div>
        ))}
      </div>
    </>
  );
}

export function WhySheet({ k, arg }) {
  useApp();
  const W = explain(k, arg, whyCtx());
  if (!W) return <Sheet title="הסבר"><div className="empty-s">אין מה להסביר כרגע</div></Sheet>;
  if (W.kind === 'rec') return <RecWhy id={W.id} />;
  if (W.kind === 'revfix') return <RevFix />;
  if (W.kind === 'recmiss') return <RecMissing />;
  return (
    <Sheet title={W.t}>
      {W.v != null && <div className="why-v" dir="ltr">{W.vs ? signed(W.v) : fMoney(W.v)}</div>}
      <div className="why-d">{W.d}</div>
      {W.rows && (
        <>
          <div className="why-lt">איך זה מחושב</div>
          <div className="why-calc">{W.rows.map((r, i) => <Row r={r} key={i} />)}</div>
        </>
      )}
      {(W.rows2 || W.d2) && (
        <>
          <div className="why-lt">{W.rt2}</div>
          {W.d2 && <div className="why-d">{W.d2}</div>}
          {W.rows2 && <div className="why-calc">{W.rows2.map((r, i) => <Row r={r} key={i} />)}</div>}
        </>
      )}
      {(W.actions || []).map((a, i) => (
        <button key={i} className="ubtn ubtn-outline w100 mt12" onClick={() => { closeModal(); setTimeout(() => navigate(a.nav), 50); }}>{a.label}</button>
      ))}
      <WhyList list={W.list} title={W.lt || 'פירוט'} />
      <WhyList list={W.list2} title={W.lt2 || ''} />
      {W.note && <div className="why-note">{W.note}</div>}
    </Sheet>
  );
}

/* ── חיוב קבוע אחד: מה הוגדר, מה ירד בפועל, ומה המצב החודש, עם פעולות לתיקון ── */
function RecWhy({ id }) {
  const r = S.rec.find((x) => String(x.id) === String(id));
  if (!r) return <Sheet title="חיוב קבוע"><div className="empty-s">החיוב נמחק</div></Sheet>;
  const M = recMatchAll(), src = recSrcOf(r), est = recEst(r), ra = Math.abs(Number(r.amount) || 0);
  const hist = (M.hist[r.id] || []).slice().sort((a, b) => (a.d < b.d ? 1 : -1)).slice(0, 3);
  const rows = [{ k: 'הסכום שהוגדר', v: ra }];
  hist.forEach((h) => rows.push({ k: 'ירד בפועל ' + wD(h.t.date), v: h.a }));
  rows.push({ k: hist.length >= 2 ? 'בתחזית (חציון 3 החיובים האחרונים)' : 'בתחזית (הסכום שהוגדר)', v: est, tot: 1 });
  const occ = M.occ.filter((o) => o.r === r && o.st !== 'old').sort((a, b) => (a.iso < b.iso ? -1 : 1)).slice(0, 3);
  const aliases = (S.set.recMatch.alias[r.id] || []);
  return (
    <Sheet title={r.description}>
      <div className="why-v" dir="ltr">{fMoney(est)}</div>
      <div className="why-d">
        {(r.type === 'income' ? 'הכנסה קבועה' : 'חיוב קבוע') + ' ב-' + r.day_of_month + ' לחודש' + (recFreqLabel(r) ? ' (' + recFreqLabel(r) + ')' : '') + ', ' + (src === 'credit' ? 'בכרטיס האשראי' : 'מהעו"ש') + '.'}
      </div>
      <div className="why-lt">איך זה מחושב</div>
      <div className="why-calc">{rows.map((x, i) => <Row r={x} key={i} />)}</div>
      <div className="why-lt">החודשים האחרונים והקרובים</div>
      <div className="why-calc">
        {occ.map((o) => (
          <div key={o.key} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
            <div className="why-row" style={{ padding: 0 }}><span>{wD(o.d)}</span><b>{recStatusTxt(o)}</b></div>
            {o.st === 'paid' && <div className="note">{o.t.description}</div>}
            <OccActions o={o} r={r} src={src} />
          </div>
        ))}
      </div>
      <button className="ubtn ubtn-outline w100 mt12" onClick={() => openRecModal(r.id)}>✏️ עריכת החיוב הקבוע</button>
      <div className="why-note">
        איך מזהים שהוא ירד: עסקה {src === 'credit' ? 'באשראי' : 'בעו"ש'} עם שם דומה, עד 12 יום מהתאריך, ובסכום סביר.
        כשהיא נמצאת, החיוב הקבוע לא נספר שוב, כי הוא כבר {src === 'credit' ? 'בתוך סך האשראי של התקופה' : 'בתוך היתרה'}.
        כשהיא לא נמצאת, הוא ממשיך להיספר גם אחרי התאריך, עד שהסריקה תראה אותו.
        {aliases.length ? ' שמות שלמדנו מקישור ידני: ' + aliases.join(', ') + '.' : ''}
      </div>
    </Sheet>
  );
}
function OccActions({ o, r, src }) {
  if (o.st === 'late' || o.st === 'missing') {
    const cand = recCandidates(o).slice(0, o.st === 'late' ? 4 : 8);
    return (
      <>
        <div className="note" style={{ margin: '4px 0' }}>
          {o.st === 'late'
            ? 'עוד לא הופיע בסריקה, ולכן הוא עדיין נספר בתחזית' + (src === 'credit' ? ' (בתוך חיוב הכרטיס).' : ' (מחר).')
            : 'הסריקה כבר מכסה כמה ימים אחרי ' + wD(o.d) + ' והוא לא נמצא. אם הוא ירד בשם אחר, בחר אותו. אם לא ירד החודש, סמן.'}
        </div>
        {cand.length > 0 && <div style={{ fontSize: '.82rem', fontWeight: 700, marginTop: 6 }}>אם הוא כבר ירד בשם אחר, בחר אותו:</div>}
        {cand.map((t) => {
          const x = wTx(t);
          return (
            <div className="why-li" key={t.id}>
              <div style={{ minWidth: 0 }}><div className="why-ln">{x.n}</div><div className="why-ls">{x.d}</div></div>
              <div className="row" style={{ gap: 6 }}>
                <b dir="ltr">{fMoney(Math.abs(x.v))}</b>
                <button className="ubtn ubtn-outline btn-xs" onClick={() => recLink(t, r.id)}>זה החיוב</button>
              </div>
            </div>
          );
        })}
        <button className="ubtn ubtn-outline w100 mt6" style={{ fontSize: '.86rem' }} onClick={() => recSkip(r.id, o.ym)}>לא ירד החודש</button>
      </>
    );
  }
  if (o.st === 'skip') return <button className="ubtn ubtn-ghost btn-xs" onClick={() => recUnskip(r.id, o.ym)}>בטל</button>;
  if (o.st === 'paid' && S.set.recMatch.link[o.t.id] != null) return <button className="ubtn ubtn-ghost btn-xs" onClick={() => recUnlink(o.t.id)}>בטל קישור</button>;
  return null;
}

function RecMissing() {
  const L = recOpen('cash').concat(recOpen('credit')).filter((o) => o.st === 'missing');
  return (
    <Sheet title="חיובים קבועים שלא נמצאו">
      <div className="why-d">הסריקה כבר מכסה כמה ימים אחרי התאריך שלהם, ולא נמצאה עסקה מתאימה. הם עדיין נספרים בתחזית, ליתר ביטחון. לחיצה על חיוב מאפשרת לבחור את העסקה או לסמן שלא ירד.</div>
      {!L.length && <div className="empty-s">✓ הכל נמצא</div>}
      {L.map((o) => (
        <div className="why-li clickable" key={o.key} onClick={() => openWhy('rec', o.r.id)}>
          <div><div className="why-ln">{o.r.description}</div><div className="why-ls">צפוי היה {wD(o.d)} · {recSrcOf(o.r) === 'credit' ? 'אשראי' : 'עו"ש'}</div></div>
          <b dir="ltr">{fMoney(o.amount)} ‹</b>
        </div>
      ))}
    </Sheet>
  );
}

function RevFix() {
  const L = revCandidates();
  return (
    <Sheet title="שמות שנראים הפוכים">
      <div className="why-d">בסריקות קודמות של העו"ש חלק מהשמות נשמרו הפוכים. כאן מוצג כל שם לצד הגרסה המתוקנת. אם שם מסוים דווקא נכון, לחץ "נכון כך".</div>
      {!L.length && <div className="empty-s">אין שמות לתיקון.</div>}
      {L.map((x) => (
        <div className="why-li" key={x.t.id}>
          <div style={{ minWidth: 0 }}><div className="why-ln">{x.rev}</div><div className="why-ls">היום: {x.t.description} · {wD(x.t.date)}</div></div>
          <button className="ubtn ubtn-ghost btn-xs" onClick={() => keepName(x.t.id)}>נכון כך</button>
        </div>
      ))}
      {L.length > 0 && <button className="ubtn ubtn-primary w100 mt12" onClick={fixReversedNames}>תקן {L.length} שמות</button>}
    </Sheet>
  );
}
